import React, { useEffect, Fragment, useRef, useState, memo } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import isEqual from "lodash/isEqual";

import {
	Button,
	Radio,
	RadioGroup,
	FormControlLabel,
	List,
	ListItem,
	ListItemText,
	Switch,
	SvgIcon,
	Typography,
	Drawer
} from "@mui/material";
import { SortableContainer, SortableElement, arrayMove } from "react-sortable-hoc";
import { mdiMenu, mdiFilterMenu } from "@mdi/js";
import Close from "@mui/icons-material/Close";

import { Translate, getTranslation } from "orion-components/i18n";
import SpriteIcon from "orion-components/SharedComponents/SpriteIcon/SpriteIcon";
import { getEntityIconsByType } from "orion-components/GlobalData/Selectors";
import { getDir } from "orion-components/i18n/Config/selector";
import { setLocalAppState } from "orion-components/AppState/Actions";
import { setFilterSettings } from "orion-components/Dock/Notifications/actions";

const propTypes = {
	handleActions: PropTypes.func,
	filterGroupBy: PropTypes.string
};

const defaultProps = {
	handleActions: null,
	filterGroupBy: "none",
	customStyles: {}
};

const styles = {
	iconStyle: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: 10,
		borderRadius: 2
	}
};

const FilterSettingsWrapper = props => {
	const colors = useSelector(state => getEntityIconsByType(state)("frame_colors"));
	const dir = useSelector(state => getDir(state));
	const filterStatus = useSelector(state => state.appState.persisted?.filterStatus);
	const eventLookups = useSelector(state => state.eventLookups);
	const alertFilterSettings = useSelector(state => state.appState.persisted?.alertFilterSettings);

	const eventLookupsType = eventLookups.types || [];
	const eventLookupsPriority = eventLookups.priorities?.sort((a, b) => a.order - b.order) || [];

	return (
		<FilterSettings
			{...props}
			colors={colors}
			dir={dir}
			filterStatus={filterStatus}
			alertFilterSettings={alertFilterSettings}
			eventLookupsType={eventLookupsType}
			eventLookupsPriority={eventLookupsPriority}
		/>
	);
};

const FilterSettings = props => {
	const dispatch = useDispatch();

	const {
		setGroupedItems,
		updateGroupedNotifications,
		filteredActiveNotification,
		customStyles,
		colors,
		dir,
		filterStatus,
		alertFilterSettings,
		eventLookupsType,
		eventLookupsPriority
	} = props;

	const [filterGroupBy, setFilterGroupBy] = useState("none");
	const [eventTypes, setEventTypes] = useState([]);
	const [eventPriorities, setEventPriorities] = useState([]);

	const SortableItem = SortableElement(({ value, enable }) => (
		// Using zIndex of 1250 to ensure that the SortableItem is always on top of the FilterSettings panel.
		<div className="list-item-wrapper" key={value.id} style={{ position: "relative", zIndex: 1250 }}>
			<ListItem
				style={{
					width: "100%",
					margin: "8px 0",
					borderRadius: 5,
					backgroundColor: "#242426",
					padding: "8px 16px 8px 10px",
					display: "flex"
				}}
			>
				<div style={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
					<Switch
						checked={value.filterEnabled}
						color="primary"
						onChange={() => {
							enable(value.id);
						}}
						sx={{
							"&.MuiSwitch-root .MuiSwitch-switchBase": {
								color: "#9F9F9F"
							},

							"&.MuiSwitch-root .Mui-checked": {
								color: "#4DB5F4"
							}
						}}
					/>
					<div
						style={{
							...styles.iconStyle,
							...{
								backgroundColor: colors[value?.color]?.color ? colors[value?.color]?.color : "#6A6C71",
								height: 30,
								width: 30,
								marginLeft: 10,
								borderRadius: 7
							}
						}}
					>
						<SpriteIcon iconName={value.icon} customHeight="17.5px" customWidth="17.5px" />
					</div>
					<ListItemText
						primary={value.name}
						style={{ marginLeft: 14, color: value?.filterEnabled ? "white" : "#6A6C71" }}
						sx={{ fontSize: 15 }}
					/>
				</div>
				<div style={{ display: "flex", justifyContent: "center" }}>
					<SvgIcon style={{ width: 23, color: "white", cursor: "grab" }}>
						<path d={mdiMenu} />
					</SvgIcon>
				</div>
			</ListItem>
		</div>
	));

	const SortableList = SortableContainer(({ items, enable }) => {
		return (
			<List style={{ paddingTop: 0 }}>
				{items?.map((value, index) => (
					<>
						<SortableItem key={`item-${value.id}`} index={index} value={value} enable={id => enable(id)} />
					</>
				))}
			</List>
		);
	});

	const handleActions = (groupBy, groupItems) => {
		setFilterGroupBy(groupBy);
		if (setGroupedItems) setGroupedItems(groupItems);
		if (groupBy == "priority") {
			setEventPriorities(groupItems.priority);
		}
		setEventTypes(groupItems.type);

		const filterValues = {
			filterGroupBy: groupBy,
			types: groupItems.type,
			priorities: groupItems.priority
		};

		dispatch(setFilterSettings(filterValues));

		if (updateGroupedNotifications) updateGroupedNotifications(groupBy, groupItems, filteredActiveNotification);
	};

	useEffect(() => {
		setEventTypes(props.eventTypes);
	}, [props.eventTypes]);

	useEffect(() => {
		if (alertFilterSettings && eventLookupsType?.length > 0) {
			// update state to reflect any changes to event lookups
			for (const type of eventLookupsType) {
				const matchingType = alertFilterSettings.types.find(filterType => filterType.id === type.id);
				type.filterEnabled = matchingType
					? matchingType.filterEnabled === undefined
						? true
						: matchingType.filterEnabled
					: true;
			}

			// applying eventLookups changes to appState eventTypes and preserving the exact ordering applied to appState.
			const eventTypeIds = alertFilterSettings.types?.map(item => item.id);
			const mergedEventTypes = eventLookupsType
				?.filter(type => type.enabled)
				?.map(type => ({
					...type,
					enabled: alertFilterSettings.types?.find(item => item.id === type.id)?.enabled ?? true
				}));
			const sortedEventTypes = mergedEventTypes?.sort(
				(a, b) => eventTypeIds.indexOf(a.id) - eventTypeIds.indexOf(b.id)
			);
			alertFilterSettings.types = sortedEventTypes;

			// filtering isn't an option for priorities but setting filterEnabled makes this consistent
			// with types and allows existing code to continue to work
			alertFilterSettings.priorities = eventLookupsPriority
				.filter(priority => priority.enabled)
				.map(priority => ({ ...priority, filterEnabled: true }));

			setFilterGroupBy(alertFilterSettings.filterGroupBy);
			setEventTypes(alertFilterSettings.types);
			setEventPriorities(alertFilterSettings.priorities);
			if (!filterStatus && alertFilterSettings.filterGroupBy != "none") {
				handleActions(alertFilterSettings.filterGroupBy, {
					type: alertFilterSettings.types,
					priority: alertFilterSettings.priorities
				});
			}
		} else {
			const enabledEventLookupsType =
				eventLookupsType?.length > 0
					? eventLookupsType.filter(type => type.enabled).map(type => ({ ...type, filterEnabled: true }))
					: [];
			setEventTypes(enabledEventLookupsType);
			const enabledEventLookupsPriority =
				eventLookupsPriority?.length > 0
					? eventLookupsPriority
							.filter(priority => priority.enabled)
							.map(priority => ({ ...priority, filterEnabled: true }))
					: [];
			setEventPriorities(enabledEventLookupsPriority);
		}
	}, []);

	const onSortEnd = ({ oldIndex, newIndex }) => {
		setEventTypes(arrayMove(eventTypes, oldIndex, newIndex));
	};

	const handleToggleWidget = id => {
		const newWidgets = eventTypes.map(widget => {
			if (widget.id === id) {
				const widgetValue = { ...widget, filterEnabled: !widget.filterEnabled };
				return widgetValue;
			} else {
				return widget;
			}
		});
		setEventTypes(newWidgets);
	};

	const handleFilterClick = () => {
		dispatch(setLocalAppState("filterStatus", true));
	};

	const closeFilterSettings = () => {
		dispatch(setLocalAppState("filterStatus", false));
	};

	const scrollRef = useRef(null);

	return (
		<>
			<SvgIcon
				style={{
					width: 20,
					height: 20,
					color: "#979798",
					cursor: "pointer",
					margin: "10px 10px -10px",
					...customStyles
				}}
				onClick={() => handleFilterClick()}
			>
				<path d={mdiFilterMenu} />
			</SvgIcon>
			<Drawer
				open={filterStatus}
				anchor={dir === "rtl" ? "left" : "right"}
				sx={{
					zIndex: 2600,
					["& .MuiDrawer-paper"]: {
						width: "420px",
						backgroundColor: "#2C2B2D",
						position: "absolute !important",
						height: "calc(100vh - 48px) !important",
						top: "48px !important",
						overflow: "visible !important"
					}
				}}
			>
				<div
					style={{
						width: "100%",
						height: "100vh",
						display: "flex",
						flexDirection: "column",
						background: "#494D53"
					}}
					onClick={() => scrollRef.current.focus()}
				>
					{/* Header with close back and done buttons */}
					<Fragment>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								backgroundColor: "#1D1D1D",
								paddingTop: 13,
								paddingBottom: 7,
								height: 44
							}}
						>
							<div
								style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
								onClick={() => closeFilterSettings()}
							>
								<i className="material-icons" style={{ marginLeft: 10, color: "#FFF" }}>
									keyboard_arrow_left
								</i>
								<div style={{ color: "#FFF", fontSize: 15, marginLeft: 9 }}>
									<Translate value="global.dock.notifications.main.filterSettings.back" />
								</div>
							</div>
							<div style={{ color: "#fff" }}>
								<Translate value="global.dock.notifications.main.filterSettings.title" />
							</div>
							<div>
								<Close
									style={{ cursor: "pointer", fontSize: 18, marginRight: 10 }}
									sx={{ color: "#FFF" }}
									onClick={() => closeFilterSettings()}
								/>
							</div>
						</div>
						<div style={{ backgroundColor: "#2C2B2D", width: "100%" }}>
							<Button
								variant="contained"
								color="primary"
								style={{
									width: "93%",
									margin: 12,
									color: "#fff",
									backgroundColor: "#6EB4ED",
									height: 35
								}}
								sx={{
									"&:hover": {
										backgroundColor: "#3076AF !important"
									}
								}}
								onClick={() => {
									closeFilterSettings();
									handleActions(filterGroupBy, { type: eventTypes, priority: eventPriorities });
								}}
							>
								<Translate value={"global.dock.notifications.main.filterSettings.doneBtn"} />
							</Button>
						</div>
					</Fragment>
					<Fragment>
						<div style={{ backgroundColor: "#494D53", padding: "10px 15px" }}>
							<div style={{ color: "#fff", lineHeight: "16px", marginTop: 10, marginBottom: 6 }}>
								<Translate value={"global.dock.notifications.main.filterSettings.groupAlarm.title"} />
							</div>
							<div style={{ marginBottom: "30px" }}>
								<RadioGroup
									row
									name="filterGroupBy"
									defaultValue="none"
									value={filterGroupBy}
									style={{ display: "flex", flexWrap: "wrap" }}
									onChange={e => setFilterGroupBy(e.target.value)}
								>
									<FormControlLabel
										value="none"
										control={
											<Radio
												sx={{
													color: "#fff",
													"&.Mui-checked": {
														color: "rgb(110, 180, 237)"
													}
												}}
											/>
										}
										label={getTranslation(
											"global.dock.notifications.main.filterSettings.groupAlarm.none"
										)}
										sx={{ minWidth: "110px", color: "#fff" }}
									/>
									<FormControlLabel
										value="priority"
										control={
											<Radio
												sx={{
													color: "#fff",
													"&.Mui-checked": {
														color: "rgb(110, 180, 237)"
													}
												}}
											/>
										}
										label={getTranslation(
											"global.dock.notifications.main.filterSettings.groupAlarm.priority"
										)}
										sx={{ minWidth: "110px", color: "#fff" }}
									/>
									<FormControlLabel
										value="type"
										control={
											<Radio
												sx={{
													color: "#fff",
													"&.Mui-checked": {
														color: "rgb(110, 180, 237)"
													}
												}}
											/>
										}
										label={getTranslation(
											"global.dock.notifications.main.filterSettings.groupAlarm.type"
										)}
										sx={{ minWidth: "110px", color: "#fff" }}
									/>
								</RadioGroup>
							</div>
							<div>
								<Typography style={{ fontSize: "16px" }} color="#fff">
									<Translate
										value={"global.dock.notifications.main.filterSettings.filterEventTypesTitle"}
									/>
								</Typography>
								<Typography style={{ fontSize: "13px" }} color="#82878C">
									<Translate
										value={"global.dock.notifications.main.filterSettings.filterEventTypesSubTitle"}
									/>
								</Typography>
							</div>
						</div>
					</Fragment>
					<div
						style={{
							backgroundColor: "#494D53",
							flexGrow: 1,
							overflow: "auto",
							marginBottom: 45,
							marginLeft: 15,
							marginRight: 15,
							outline: "none"
						}}
						tabIndex={0}
						ref={scrollRef}
					>
						{eventTypes && (
							<SortableList
								items={eventTypes}
								onSortEnd={onSortEnd}
								distance={1}
								enable={id => handleToggleWidget(id)}
							/>
						)}
					</div>
				</div>
			</Drawer>
		</>
	);
};

FilterSettingsWrapper.propTypes = propTypes;
FilterSettingsWrapper.defaultProps = defaultProps;

export default memo(FilterSettingsWrapper, (prevProps, nextProps) => {
	if (!isEqual(prevProps, nextProps)) {
		return false;
	}
	return true;
});
